import { useMemo } from 'react';
import { Provider } from 'react-redux';
import {
  RouterProvider,
  createBrowserRouter,
  createHashRouter,
} from 'react-router';

import { isElectron } from '@fly/shared';
import {
  SystemThemeProvider,
  useFeatureToggle,
  useSystemTheme,
} from '@fly/shared-hooks';
import { FlyLoader, FlyToastContainer } from '@fly/shared-ui';
import { store } from '@fly/store';
// Storybook preview imports webapp styles while also using SvgSpriteLoader from @fly/svg; Nx reports a false-positive boundary cycle for webapp → root → preview → webapp.
// eslint-disable-next-line @nx/enforce-module-boundaries -- SvgSpriteLoader must live in @fly/svg with sprite asset; see libs/svg/SvgSpriteLoader.tsx
import { SvgSpriteLoader } from '@fly/svg';
import '@fly/translation';
import { Theme } from '@radix-ui/themes';
import log from 'loglevel';
import { register } from 'timeago.js';
import _enShort from 'timeago.js/lib/lang/en_short';

import { useAppRoutes } from './AppRoutes';
import { FullStoryInit } from './FullStoryInit';
import OidcProviderWithSessionLost from './OidcProviderWithSessionLost';
import './styles';

// Vite 8 CJS interop: default import may be the entire module.exports object
const enShort =
  typeof _enShort === 'function'
    ? _enShort
    : 'default' in (_enShort as object)
      ? (_enShort as any).default
      : _enShort;
if (typeof enShort !== 'function') {
  throw new Error(
    `timeago.js en_short locale resolved to ${typeof enShort} — CJS interop shim needs updating`
  );
}

const isDev = process.env.NODE_ENV !== 'production';
log.setLevel(isDev ? 'debug' : 'silent');

// Register languages for time ago library
register('en_short', enShort);

export default function App() {
  return (
    <Provider store={store}>
      <SystemThemeProvider>
        <Theme appearance='inherit' accentColor='indigo' grayColor='slate'>
          <AppContent />
        </Theme>
        <SvgSpriteLoader />
      </SystemThemeProvider>
    </Provider>
  );
}

export function AppContent() {
  const { isLoading } = useFeatureToggle();

  if (isLoading) {
    return <FlyLoader />;
  }

  return <ReadyAppContent />;
}

function ReadyAppContent() {
  const { currentTheme } = useSystemTheme();
  const appRoutes = useAppRoutes();
  const router = useMemo(
    () =>
      isElectron()
        ? createHashRouter(appRoutes, { basename: '' })
        : createBrowserRouter(appRoutes, { basename: '' }),
    [appRoutes]
  );

  return (
    <>
      <FlyToastContainer />
      <OidcProviderWithSessionLost>
        <FullStoryInit />
        <div
          data-theme={currentTheme}
          id='app-wrapper'
          className='align-center flex h-full justify-center'
        >
          <RouterProvider router={router} />
          <div id='overlay-portal' />
        </div>
      </OidcProviderWithSessionLost>
    </>
  );
}
